import type { ChatMode, ChatModeState, ChatModeTransitionReason } from '../types/chat-mode';
import { DEFAULT_CHAT_MODE } from '../const/mode-config';
import { useMediaStore } from '@/shared/store/voice-chat';
import { useVadStore } from '@/shared/store/vad';

/**
 * 채팅 모드 상태 관리 매니저
 *
 * 책임:
 * - 모드 전환 시 관련 상태 동기화
 * - 이전 모드 추적
 * - 모드 전환 로직 캡슐화
 */

export interface ChatModeManagerState {
  current: ChatMode;
  previous: ChatMode | null;
  lastTransitionReason: ChatModeTransitionReason | null;
}

/**
 * 초기 상태 생성
 */
export const createInitialChatModeState = (): ChatModeManagerState => ({
  current: DEFAULT_CHAT_MODE,
  previous: null,
  lastTransitionReason: null,
});

/**
 * 모드 전환 처리
 */
export const transitionToMode = (
  currentState: ChatModeManagerState,
  newMode: ChatMode,
  reason: ChatModeTransitionReason = 'user_initiated'
): ChatModeManagerState => {
  // 같은 모드면 변경 없음
  if (currentState.current === newMode) {
    return currentState;
  }

  return {
    current: newMode,
    previous: currentState.current,
    lastTransitionReason: reason,
  };
};

/**
 * 이전 모드로 복원
 */
export const restoreToPreviousMode = (
  currentState: ChatModeManagerState
): ChatModeManagerState => {
  if (!currentState.previous) {
    return currentState;
  }

  return {
    current: currentState.previous,
    previous: currentState.current,
    lastTransitionReason: 'auto_restored',
  };
};

/**
 * 모드 전환 시 관련 스토어 상태 동기화
 */
export const syncStoresForMode = (mode: ChatMode): void => {
  const { setIsMuted } = useMediaStore.getState();
  const { setVadStatus } = useVadStore.getState();

  if (mode === 'voice') {
    // 음성 모드: 마이크 활성화, VAD 활성화
    setIsMuted(false);
    setVadStatus('listening');
    console.log('🎤 [CHAT_MODE] 음성 모드 전환 - 마이크/VAD 활성화');
  } else {
    // 채팅 모드: 마이크 비활성화, VAD 비활성화
    setIsMuted(true);
    setVadStatus('paused');
    console.log('⌨️ [CHAT_MODE] 채팅 모드 전환 - 마이크/VAD 비활성화');
  }
};

/**
 * 모드 복원 전 상태 저장
 * 나중에 복원할 때 사용
 */
export const saveCurrentModeForRestoration = (
  currentState: ChatModeManagerState
): ChatModeManagerState => ({
  ...currentState,
  previous: currentState.current,
});
